import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { useAtomValue } from 'jotai';

import { useIsOfflineRecordingEnabled } from '@/app-shell/flags/desktop-flags';
import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { isTauri } from '@/tauri/utils';

import { useNavigatorOnline } from './useNavigatorOnline';

/**
 * Reactive predicate: is the app currently offline for offline-recording
 * purposes? This is the connectivity half only — it deliberately does *not*
 * fold in whether this device can actually capture (the cached-identity gate),
 * so it can power both the "grey out the controls" and the "you must log in
 * online first" branches in {@link useOfflineGating}.
 *
 * Offline recording is desktop-only and behind `useIsOfflineRecordingEnabled()`
 * per the rollout convention — the flag is the one master switch, so this stays
 * live on production builds when the flag is on. When those hold, "offline" is
 * driven by the real connectivity monitor (`offline` / `backend_unreachable`).
 * The interim dev toggle (`window.__heidiOfflineTest`, APP-6344) is a non-prod
 * affordance only — read at render time and never honoured on a prod build, so
 * it can't act as a live offline override in production.
 *
 * Shared source of truth with {@link useIsOfflineCaptureMode}, which layers the
 * cached-identity requirement on top.
 */
export function useIsOfflineConnectivity(): boolean {
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const connectivity = useAtomValue(offlineConnectivityStateAtom);
  // Subscribed rather than read during render, so the "reactive predicate" in
  // the docblock above is true of the browser half too. Called before the early
  // return to keep hook order unconditional.
  const browserIsOnline = useNavigatorOnline();

  if (!isOfflineRecordingEnabled || !isTauri()) {
    return false;
  }

  // The interim dev toggle stays scoped to non-prod so it never becomes a live
  // offline override on a shipped production build. The real connectivity
  // signals below remain active in prod when the flag is on.
  const offlineTestModeOn =
    getEnvironment() !== ENV_NAME.PROD &&
    (window as { __heidiOfflineTest?: boolean }).__heidiOfflineTest === true;

  // Synchronous fast-path: recognise wifi-off instantly before the native probe
  // catches up, but never let an unreliable WebView false-negative override a
  // confirmed Rust `online_ready_to_sync` reading.
  const browserOfflineWithoutConfirmedNativeOnline =
    !browserIsOnline && connectivity !== 'online_ready_to_sync';

  return (
    connectivity === 'offline' ||
    connectivity === 'backend_unreachable' ||
    browserOfflineWithoutConfirmedNativeOnline ||
    offlineTestModeOn
  );
}
